import useSWR from 'swr';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { swrFetcher } from 'contexts/user';
import { insertIf, join } from 'lib/helpers';
import { fetchAvatarUrl } from 'lib/api/owner_api';
import usePermission from 'components/profile/usePermission';
import { LeadingArea, WideContentArea } from './AccountLayout';
import { BackArrow } from 'components/common/svgIcons';
import styles from './TeamNav.module.css';
import { useWindowSize } from 'lib/deviceUtils';
import { ValueCallback, VoidCallback } from 'types/callbacks';
import AccountDivider from './AccountDivider';
import { ProfileResponse } from 'components/login/types';

interface Props {
  manageAvatar?: boolean;
  backButton?: boolean;
  backButtonUrl?: string;
}

const TeamNav = ({
  manageAvatar = false,
  backButton = false,
  backButtonUrl,
}: Props) => {
  const router = useRouter();
  const id = parseInt(router.query.id as string);
  const { data: team } = useSWR(`/api/teams/${id}`, swrFetcher);
  const { isAdmin } = usePermission(id);
  const width = useWindowSize().width;

  return (
    <>
      <AccountDivider />
      {backButton && width > 720 && (
        <LeadingArea>
          <BackButton
            onClick={() => router.push(backButtonUrl ?? `/account/${id}`)}
          />
        </LeadingArea>
      )}
      <WideContentArea>
        <div className={styles.TeamHeader}>
          {backButton && width <= 720 && (
            <BackButton
              onClick={() => router.push(backButtonUrl ?? `/account/${id}`)}
            />
          )}
          <div className={styles.TeamAvatarContainer}>
            <img
              className={join([
                styles.Avatar,
                ...insertIf(!team?.avatar, styles.AvatarFallback),
              ])}
              src={team?.avatar ?? '/static/upload.png'}
              alt="Avatar"
            />
          </div>
          <div className={styles.TextContainer}>
            <h1>{team?.name ?? team?.username}</h1>
            {manageAvatar && isAdmin && <AvatarUploadButton id={id} />}
          </div>
        </div>
      </WideContentArea>
    </>
  );
};

export default TeamNav;

interface BackProps {
  onClick: VoidCallback;
}

const BackButton = ({ onClick }: BackProps) => (
  <div className={styles.BackButton} onClick={onClick}>
    <BackArrow />
  </div>
);

export const UserHeader = () => {
  const { data: profile } = useSWR<ProfileResponse>('/api/profile', swrFetcher);
  const [avatarUrl, setAvatarUrl] = useState<string | null | undefined>(
    profile?.avatar
  );

  useEffect(() => setAvatarUrl(profile?.avatar), [profile]);

  return (
    <>
      <AccountDivider />
      <WideContentArea>
        <div className={styles.TeamHeader}>
          <div className={styles.TeamAvatarContainer}>
            <img
              className={join([
                styles.Avatar,
                ...insertIf(!avatarUrl, styles.AvatarFallback),
              ])}
              src={avatarUrl ?? '/static/upload.png'}
              alt="Avatar"
            />
          </div>
          <div className={styles.TextContainer}>
            <h1>{profile?.name ?? profile?.username ?? profile?.email}</h1>
            <AvatarUploadButton onChange={(url) => setAvatarUrl(url)} />
          </div>
        </div>
      </WideContentArea>
    </>
  );
};

interface ButtonProps {
  id?: number;
  onChange?: ValueCallback<string>;
}

const AvatarUploadButton = ({ id, onChange }: ButtonProps) => {
  const [isUploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const uploadAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    e?.preventDefault();
    setError(null);
    setUploading(true);
    const reader = new FileReader();
    const file = e.target.files![0];

    if (file == null) {
      setUploading(false);
      return;
    }

    if (file.size > 1000000) {
      setError('Image must be less that 1MB.');
      setUploading(false);
      return;
    }

    reader.onload = async () => {
      const url = await fetchAvatarUrl(reader.result, id);
      if (!url) {
        setError('Something went wrong.');
      }
      if (onChange) {
        onChange(url);
      }
      setUploading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  return isUploading ? (
    <p>Uploading...</p>
  ) : (
    <div style={{ display: 'flex' }}>
      {error && <div className={styles.Error}>{error}</div>}
      <label className={styles.UploadFileLabel}>
        <input type="file" accept="image/*" onChange={uploadAvatar} />
        Upload Avatar
      </label>
    </div>
  );
};
